
import { phases, allMonths } from '../../../mocks/roadmapData';

const phaseColorMap: Record<string, { bg: string; gradient: string; light: string }> = {
  rose: { bg: 'bg-rose-500', gradient: 'from-rose-400 to-rose-600', light: 'bg-rose-100' },
  amber: { bg: 'bg-amber-500', gradient: 'from-amber-400 to-amber-600', light: 'bg-amber-100' },
  teal: { bg: 'bg-teal-600', gradient: 'from-teal-400 to-teal-600', light: 'bg-teal-100' },
  emerald: { bg: 'bg-emerald-600', gradient: 'from-emerald-400 to-emerald-600', light: 'bg-emerald-100' },
  cyan: { bg: 'bg-cyan-600', gradient: 'from-cyan-400 to-cyan-600', light: 'bg-cyan-100' },
};

export default function IntegratedGantt() {
  const totalMonths = allMonths.length;

  const allMilestones = phases.flatMap((phase) =>
    phase.milestones.map((ms) => ({
      ...ms,
      color: phase.color,
    }))
  );

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">통합 간트 차트</h2>
          <p className="text-base text-gray-500 max-w-2xl mx-auto">
            5개 단계의 전체 일정을 하나의 차트로 통합하여 단계 간 연계와 중첩 구간을 한눈에 파악합니다.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <div className="min-w-[1100px]">
              {/* Year Headers */}
              <div className="flex border-b border-gray-200">
                <div className="w-56 flex-shrink-0 px-6 py-3 bg-gray-50 border-r border-gray-200"></div>
                <div className="flex-1 flex">
                  {['2025', '2026', '2027', '2028'].map((year) => {
                    const monthsInYear = allMonths.filter((m) => m.startsWith(year.slice(2)));
                    return (
                      <div
                        key={year}
                        className="text-center py-3 text-sm font-bold text-gray-700 bg-gray-50 border-r border-gray-200"
                        style={{ width: `${(monthsInYear.length / totalMonths) * 100}%` }}
                      >
                        {year}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Month Headers */}
              <div className="flex border-b border-gray-100">
                <div className="w-56 flex-shrink-0 px-6 py-2 bg-gray-50 border-r border-gray-100">
                  <span className="text-xs font-semibold text-gray-400 uppercase">단계</span>
                </div>
                <div className="flex-1 flex">
                  {allMonths.map((month, idx) => (
                    <div
                      key={idx}
                      className="flex-1 text-center py-2 text-xs text-gray-400 border-r border-gray-50 bg-gray-50/50"
                      style={{ minWidth: '22px' }}
                    >
                      {idx % 3 === 0 ? month.split('.')[1] : ''}
                    </div>
                  ))}
                </div>
              </div>

              {/* Phase Bars */}
              {phases.map((phase) => {
                const colors = phaseColorMap[phase.color];
                return (
                  <div key={phase.id} className="flex border-b border-gray-50 hover:bg-gray-50/30 transition-colors">
                    <div className="w-56 flex-shrink-0 px-6 py-4 border-r border-gray-100">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${colors.bg} flex-shrink-0`}></div>
                        <div>
                          <div className="text-sm font-bold text-gray-900">{phase.phase}</div>
                          <div className="text-xs text-gray-500 mt-0.5">{phase.title}</div>
                        </div>
                      </div>
                    </div>
                    <div className="flex-1 relative">
                      {/* Grid */}
                      <div className="absolute inset-0 flex">
                        {allMonths.map((_, idx) => (
                          <div key={idx} className="flex-1 border-r border-gray-50" style={{ minWidth: '22px' }}></div>
                        ))}
                      </div>
                      {/* Phase Bar */}
                      <div className="absolute inset-y-0 flex items-center py-2">
                        <div
                          className="absolute"
                          style={{
                            left: `${(phase.startMonth / totalMonths) * 100}%`,
                            width: `${((phase.endMonth - phase.startMonth + 1) / totalMonths) * 100}%`,
                          }}
                        >
                          <div className={`h-8 rounded-lg bg-gradient-to-r ${colors.gradient} shadow-sm relative overflow-hidden`}>
                            {/* Progress fill */}
                            <div
                              className="absolute inset-y-0 left-0 bg-white/20 rounded-l-lg"
                              style={{ width: `${phase.progress}%` }}
                            ></div>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <span className="text-xs font-bold text-white drop-shadow-sm">{phase.progress}%</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Milestone Row */}
              <div className="flex border-t border-gray-200">
                <div className="w-56 flex-shrink-0 px-6 py-4 border-r border-gray-100 bg-gray-50/50">
                  <div className="flex items-center gap-2">
                    <i className="ri-flag-fill text-sm text-rose-500"></i>
                    <span className="text-sm font-bold text-gray-700">마일스톤</span>
                  </div>
                </div>
                <div className="flex-1 relative bg-gray-50/30" style={{ height: '52px' }}>
                  <div className="absolute inset-0 flex">
                    {allMonths.map((_, idx) => (
                      <div key={idx} className="flex-1 border-r border-gray-50" style={{ minWidth: '22px' }}></div>
                    ))}
                  </div>
                  {allMilestones.map((ms, idx) => {
                    const colors = phaseColorMap[ms.color];
                    const leftPercent = ((ms.month + 0.5) / totalMonths) * 100;
                    return (
                      <div
                        key={idx}
                        className="absolute top-1/2 -translate-y-1/2 z-10 group cursor-default"
                        style={{ left: `${leftPercent}%`, transform: 'translateX(-50%) translateY(-50%)' }}
                      >
                        <div className={`w-4 h-4 rotate-45 ${ms.done ? 'bg-teal-500' : colors.bg} border-2 border-white shadow-md`}></div>
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-30">
                          <div className="bg-gray-900 text-white rounded-lg px-3 py-2 shadow-xl whitespace-nowrap">
                            <div className="text-xs font-bold">{ms.name}</div>
                            <div className="text-xs text-gray-400">{allMonths[ms.month]}</div>
                            <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-[5px] border-r-[5px] border-t-[5px] border-transparent border-t-gray-900"></div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Phase Legend */}
        <div className="flex flex-wrap justify-center gap-6 mt-8">
          {phases.map((phase) => {
            const colors = phaseColorMap[phase.color];
            return (
              <div key={phase.id} className="flex items-center gap-2">
                <div className={`w-4 h-3 rounded ${colors.bg}`}></div>
                <span className="text-sm text-gray-600">{phase.phase} {phase.title}</span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
